<template>
  <div class="review-response-root">
    <div class="header-main">
      <div class="hm-left">
        <div>
          <div class="bread-crumb"></div>
          <div class="header-main__title-wrap">
            <h1 class="header-main__title">Ответы на отзывы</h1>
            <div class="header-main__review"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Блок 1: Отзыв, тикет, роль, тон -->
    <div class="field-rules-section">
      <h2 class="field-rules-title">Отзыв и ответственный</h2>
      <div class="field-rule-block">
        <div class="field-rule-row">
          <label class="field-rule-label">Тикет (кто даёт ответ, дублируется в конце)</label>
          <input
            v-model="ticket"
            type="text"
            class="field-rule-input"
            placeholder="Например: Петкевич А.В., 05.02.2026"
          />
        </div>
        <div class="field-rule-row field-rule-row-inline">
          <label class="field-rule-label">Роль ответственного</label>
          <select v-model="responseRole" class="field-rule-select">
            <option value="">— Выберите —</option>
            <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.label }}</option>
          </select>
        </div>
        <div class="field-rule-row">
          <label class="field-rule-label">Текст отзыва клиента</label>
          <template v-if="showHighlightedReview && hasProcessedReview && reviewText.trim() && botDetectedClaimsWithPhrase.length">
            <div class="review-highlight-inplace" v-html="highlightedReviewHtml" />
            <button type="button" class="review-edit-text-btn" @click="showHighlightedReview = false">
              Изменить текст отзыва
            </button>
          </template>
          <template v-else>
            <textarea
              v-model="reviewText"
              class="field-rule-textarea"
              rows="6"
              placeholder="Вставьте отзыв с претензиями..."
            />
          </template>
        </div>
        <div v-if="reviewText.trim()" class="field-rule-row">
          <button
            type="button"
            class="process-btn"
            :disabled="processingClaims"
            @click="processReview"
          >
            {{ processingClaims ? 'Обработка…' : 'Обработать' }}
          </button>
        </div>
        <div v-if="hasProcessedReview && (allClaimTagsForBar.length || complaints.length)" class="field-rule-row detected-claims-row">
          <label class="field-rule-label">Определил бот / отмеченные претензии</label>
          <div class="detected-claims-tags">
            <span
              v-for="(item, i) in allClaimTagsForBar"
              :key="'tag-' + (item.id || '') + '-' + i"
              :class="['claim-tag', item.colorClass]"
              :title="getClaimTagTitle(item)"
            >
              {{ item.label }}
              <button
                type="button"
                class="claim-tag-remove"
                title="Убрать претензию из выбранных"
                aria-label="Убрать"
                @click.stop="removeClaimTag(item)"
              >×</button>
            </span>
            <div class="add-claim-wrap">
              <button
                type="button"
                class="add-claim-btn"
                title="Добавить претензию из списка (которую бот не определил)"
                @click="showAddClaimSelect = !showAddClaimSelect"
              >+</button>
              <select
                v-if="showAddClaimSelect"
                v-model="addClaimSelectedId"
                class="field-rule-select add-claim-select"
                @change="onAddClaimFromList"
              >
                <option value="">— Выберите претензию —</option>
                <option v-for="c in uncheckedComplaints" :key="c.id" :value="c.id">{{ c.label }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Блок: Примеры ответа -->
    <div class="field-rules-section">
      <h2 class="field-rules-title">Примеры ответа</h2>
      <div class="field-rule-block examples-block">
        <div v-for="ex in responseExamples" :key="ex.id" class="example-item">
          <strong class="example-item-name">{{ ex.name }}</strong>
          <p class="example-item-text">{{ ex.text }}</p>
        </div>
        <div v-if="!responseExamples.length" class="example-item example-item-empty">Пока нет примеров.</div>
      </div>
      <div class="add-example-row">
        <button
          v-if="!showAddExampleForm"
          type="button"
          class="add-complaint-catalog-btn"
          @click="showAddExampleForm = true"
        >
          + Добавить пример ответа
        </button>
        <div v-else class="add-complaint-catalog-form">
          <div class="field-rule-row">
            <label class="field-rule-label">Название примера</label>
            <input
              v-model="newExampleName"
              type="text"
              class="field-rule-input"
            />
          </div>
          <div class="field-rule-row">
            <label class="field-rule-label">Текст примера</label>
            <textarea
              v-model="newExampleText"
              class="field-rule-textarea add-complaint-textarea"
              rows="4"
            />
          </div>
          <div class="add-complaint-catalog-actions">
            <button
              type="button"
              class="save-matrix-btn"
              :disabled="!newExampleName.trim() || !newExampleText.trim() || addingNewExample"
              @click="addNewExample"
            >
              {{ addingNewExample ? 'Сохранение…' : 'Сохранить' }}
            </button>
            <button
              type="button"
              class="add-complaint-cancel-btn"
              :disabled="addingNewExample"
              @click="cancelAddExampleForm"
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Блок 2a: Предустановленные претензии (до обработки — все, после — только отмеченные ботом) -->
    <div class="field-rules-section">
      <h2 class="field-rules-title">Основные претензии и ответы на них</h2>
      <p class="matrix-hint">
        <template v-if="hasProcessedReview">Показаны претензии, совпавшие с отзывом. Выберите опровержение.</template>
        <template v-else>Список предустановленных претензий. После нажатия «Обработать» здесь останутся только те, что есть в отзыве. Кнопкой «+» можно добавить новую претензию в справочник.</template>
      </p>
      <template v-if="complaints.length">
        <div
          v-for="complaint in (hasProcessedReview ? checkedComplaints : complaints)"
          :key="complaint.id"
          class="complaint-row"
        >
          <div class="complaint-left">
            <label class="complaint-check">
              <input type="checkbox" v-model="complaint.checked" />
              <span>{{ complaint.label }}</span>
            </label>
          </div>
          <div class="complaint-right complaint-right-fields">
            <select v-model="complaint.selectedTone" class="field-rule-select complaint-select complaint-tone" @change="onComplaintToneChange(complaint)">
              <option value="">— Характер ответа —</option>
              <option v-for="t in tones" :key="t.id" :value="t.id">{{ t.label }}</option>
            </select>
            <select v-model="complaint.selectedRebuttal" class="field-rule-select complaint-select" :disabled="!complaint.selectedTone">
              <option value="">— Вариант ответа —</option>
              <option v-for="opt in rebuttalOptionsFor(complaint)" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
              <option value="__custom__">Свой вариант (ввести ниже)</option>
            </select>
            <template v-if="complaint.selectedRebuttal === '__custom__'">
              <input
                v-model="complaint.customRebuttal"
                type="text"
                class="field-rule-input complaint-custom"
                placeholder="Введите опровержение..."
              />
              <button
                type="button"
                class="add-rebuttal-btn"
                title="Сохранить вариант в заготовки"
                :disabled="!complaint.selectedTone || !complaint.customRebuttal.trim() || complaint.addingRebuttal"
                @click="addRebuttalToPredefined(complaint)"
              >
                {{ complaint.addingRebuttal ? '…' : '+' }}
              </button>
            </template>
            <div class="complaint-example-wrap">
              <button
                type="button"
                class="add-example-to-complaint-btn"
                title="Добавить пример к ответу на эту претензию"
                :class="{ active: showExampleSelectFor === complaint.id }"
                @click="showExampleSelectFor = (showExampleSelectFor === complaint.id ? null : complaint.id)"
              >
                +
              </button>
              <select
                v-if="showExampleSelectFor === complaint.id"
                v-model="complaint.selectedExampleId"
                class="field-rule-select complaint-example-select"
                @change="showExampleSelectFor = null"
              >
                <option value="">— Выберите пример —</option>
                <option v-for="ex in responseExamples" :key="ex.id" :value="ex.id">{{ ex.name }}</option>
              </select>
              <span v-if="complaint.selectedExampleId && getExampleById(complaint.selectedExampleId)" class="example-attached-tag">
                {{ getExampleById(complaint.selectedExampleId).name }}
                <button type="button" class="claim-tag-remove" aria-label="Убрать пример" @click="complaint.selectedExampleId = ''">×</button>
              </span>
            </div>
          </div>
        </div>
      </template>
      <div class="add-complaint-catalog-row">
        <button
          v-if="!showAddComplaintForm"
          type="button"
          class="add-complaint-catalog-btn"
          @click="showAddComplaintForm = true"
        >
          + Добавить претензию в справочник
        </button>
        <div v-else class="add-complaint-catalog-form">
          <div class="field-rule-row">
            <label class="field-rule-label">Название претензии</label>
            <input
              v-model="newComplaintLabel"
              type="text"
              class="field-rule-input"
            />
          </div>
          <div class="field-rule-row">
            <label class="field-rule-label">Характер ответа (к какому тону относится первый вариант)</label>
            <select v-model="newComplaintTone" class="field-rule-select">
              <option v-for="t in tones" :key="t.id" :value="t.id">{{ t.label }}</option>
            </select>
          </div>
          <div class="field-rule-row">
            <label class="field-rule-label">Первый вариант ответа (опционально)</label>
            <textarea
              v-model="newComplaintRebuttal"
              class="field-rule-textarea add-complaint-textarea"
              rows="3"
            />
          </div>
          <div class="add-complaint-catalog-actions">
            <button
              type="button"
              class="save-matrix-btn"
              :disabled="!newComplaintLabel.trim() || addingNewComplaint"
              @click="addNewComplaintToCatalog"
            >
              {{ addingNewComplaint ? 'Сохранение…' : 'Сохранить' }}
            </button>
            <button
              type="button"
              class="add-complaint-cancel-btn"
              :disabled="addingNewComplaint"
              @click="cancelAddComplaintForm"
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Блок 2b: Дополнительные претензии от бота (все, что бот добавил — показываются всегда после обработки) -->
    <div v-if="hasProcessedReview && processedClaims.length" class="field-rules-section">
      <h2 class="field-rules-title">Дополнительные претензии (по результатам обработки ботом)</h2>
      <p class="matrix-hint">Претензии, которых нет в предустановленном списке. Отметьте нужные и введите опровержение или добавьте в предустановленные кнопкой «+».</p>
      <div
        v-for="(complaint, idx) in processedClaims"
        :key="'bot-' + idx"
        class="complaint-row"
      >
        <div class="complaint-left">
          <label class="complaint-check">
            <input type="checkbox" v-model="complaint.checked" />
            <span>{{ complaint.label }}</span>
          </label>
        </div>
        <div class="complaint-right complaint-right-fields">
          <select v-model="complaint.selectedTone" class="field-rule-select complaint-select complaint-tone" @change="onComplaintToneChange(complaint)">
            <option value="">— Характер ответа —</option>
            <option v-for="t in tones" :key="t.id" :value="t.id">{{ t.label }}</option>
          </select>
          <template v-if="rebuttalOptionsFor(complaint).length">
            <select v-model="complaint.selectedRebuttal" class="field-rule-select complaint-select" :disabled="!complaint.selectedTone">
              <option value="">— Вариант ответа —</option>
              <option v-for="opt in rebuttalOptionsFor(complaint)" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
              <option value="__custom__">Свой вариант (ввести ниже)</option>
            </select>
            <input
              v-if="complaint.selectedRebuttal === '__custom__'"
              v-model="complaint.customRebuttal"
              type="text"
              class="field-rule-input complaint-custom"
              placeholder="Введите опровержение..."
            />
          </template>
          <input
            v-else
            v-model="complaint.customRebuttal"
            type="text"
            class="field-rule-input complaint-custom"
            placeholder="Введите опровержение или ответ..."
          />
          <div class="complaint-example-wrap">
            <button
              type="button"
              class="add-example-to-complaint-btn"
              title="Добавить пример к ответу на эту претензию"
              :class="{ active: showExampleSelectFor === 'bot-' + idx }"
              @click="showExampleSelectFor = (showExampleSelectFor === 'bot-' + idx ? null : 'bot-' + idx)"
            >
              +
            </button>
            <select
              v-if="showExampleSelectFor === 'bot-' + idx"
              v-model="complaint.selectedExampleId"
              class="field-rule-select complaint-example-select"
              @change="showExampleSelectFor = null"
            >
              <option value="">— Выберите пример —</option>
              <option v-for="ex in responseExamples" :key="ex.id" :value="ex.id">{{ ex.name }}</option>
            </select>
            <span v-if="complaint.selectedExampleId && getExampleById(complaint.selectedExampleId)" class="example-attached-tag">
              {{ getExampleById(complaint.selectedExampleId).name }}
              <button type="button" class="claim-tag-remove" aria-label="Убрать пример" @click="complaint.selectedExampleId = ''">×</button>
            </span>
          </div>
          <button
            type="button"
            class="add-to-predefined-btn"
            title="Добавить в предустановленные претензии"
            :disabled="complaint.adding"
            @click="addToPredefined(complaint, idx)"
          >
            {{ complaint.adding ? '…' : '+' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Кнопка генерации ответа -->
    <div class="field-rules-section">
      <div class="target-length-row">
        <label class="field-rule-label">Длина ответа: <strong>{{ targetResponseLengthChars }} знаков</strong></label>
        <input
          v-model.number="targetResponseLengthChars"
          type="range"
          min="500"
          max="3000"
          step="100"
          class="target-length-slider"
        />
      </div>
      <button
        type="button"
        class="save-matrix-btn generate-response-btn"
        :disabled="generating"
        @click="generateResponse"
      >
        {{ generating ? 'Генерация…' : 'Сгенерировать ответ' }}
      </button>
    </div>

    <!-- Сгенерированный черновик и правки -->
    <div v-if="draftResponse" class="field-rules-section">
      <h2 class="field-rules-title">Черновик ответа</h2>
      <p class="matrix-hint">Выделите фрагмент в тексте ниже и выберите действие: жёлтый — изменить, красный — исключить, синий — вставить свой текст после выделенного.</p>
      <!-- Ползунок длины ответа (перегенерация в % от текущего) — пока скрыт -->
      <div v-if="false" class="length-regenerate-block">
        <label class="length-regenerate-label">
          <span>Длина ответа: <strong>{{ responseLengthPercent }}%</strong></span>
        </label>
        <div class="length-regenerate-row">
          <input
            v-model.number="responseLengthPercent"
            type="range"
            min="25"
            max="100"
            step="5"
            class="length-regenerate-slider"
          />
          <span class="length-regenerate-value">{{ responseLengthPercent }}%</span>
        </div>
        <p class="matrix-hint length-hint">Укажите желаемую длину в процентах от текущего текста и нажмите кнопку — ответ будет перегенерирован с учётом этого размера.</p>
        <button
          type="button"
          class="save-matrix-btn length-regenerate-btn"
          :disabled="generating"
          @click="regenerateWithLength"
        >
          {{ generating ? 'Генерация…' : 'Перегенерировать с учётом длины' }}
        </button>
      </div>
      <div class="highlight-toolbar">
        <button type="button" class="hl-btn hl-btn--yellow" :class="{ active: highlightMode === 'change' }" @click="setHighlightMode('change')">Изменить</button>
        <button type="button" class="hl-btn hl-btn--red" :class="{ active: highlightMode === 'exclude' }" @click="setHighlightMode('exclude')">Исключить</button>
        <button type="button" class="hl-btn hl-btn--blue" :class="{ active: highlightMode === 'add' }" @click="setHighlightMode('add')">Добавить</button>
        <input v-if="highlightMode === 'add'" v-model="addFragmentText" type="text" class="hl-fragment-input" placeholder="Текст для вставки..." />
        <input v-if="highlightMode === 'change'" v-model="addFragmentText" type="text" class="hl-fragment-input" placeholder="Заменить на..." />
      </div>
      <div
        ref="draftArea"
        class="draft-area"
        :class="{ 'selecting': highlightMode }"
        @mouseup="onDraftMouseUp"
      >{{ draftDisplayText }}</div>
      <div v-if="edits.length" class="edits-list">
        <span class="edits-list-title">Правки:</span>
        <span v-for="(ed, i) in edits" :key="i" class="edit-badge">
          <template v-if="ed.type === 'exclude'">искл. «{{ ed.text.slice(0, 30) }}{{ ed.text.length > 30 ? '…' : '' }}»</template>
          <template v-else-if="ed.type === 'change'">заменить «{{ ed.text.slice(0, 20) }}{{ ed.text.length > 20 ? '…' : '' }}» → «{{ (ed.replacement || '').slice(0, 20) }}{{ (ed.replacement || '').length > 20 ? '…' : '' }}»</template>
          <template v-else-if="ed.type === 'add'">добавить после «…»: «{{ (ed.insertText || '').slice(0, 20) }}…»</template>
        </span>
      </div>
      <div class="draft-actions">
        <button
          type="button"
          class="save-matrix-btn"
          @click="generateFinalResponse"
        >
          Сгенерировать финальный ответ
        </button>
      </div>
    </div>

    <!-- Финальный ответ -->
    <div v-if="finalResponse" class="field-rules-section final-block">
      <h2 class="field-rules-title">Финальный ответ на отзыв</h2>
      <div class="final-text">{{ finalResponse }}</div>
      <div v-if="ticket" class="ticket-footer">— {{ ticket }}</div>
    </div>

    <div v-if="loadError" class="matrix-loading">{{ loadError }}</div>
  </div>
</template>

<script>
const AI_API_URL = 'https://ai.1akb.by';

const CLAIM_HIGHLIGHT_COLORS = [
  { bg: '#ffebee', border: '#ef9a9a' },
  { bg: '#e3f2fd', border: '#90caf9' },
  { bg: '#e8f5e9', border: '#a5d6a7' },
  { bg: '#fff3e0', border: '#ffcc80' },
  { bg: '#f3e5f5', border: '#ce93d8' },
  { bg: '#e0f7fa', border: '#80deea' },
  { bg: '#fce4ec', border: '#f48fb1' },
  { bg: '#e8eaf6', border: '#9fa8da' },
];

export default {
  name: 'ReviewResponse',
  data() {
    return {
      ticket: '',
      responseRole: '',
      reviewText: '',
      roles: [],
      tones: [],
      complaints: [],
      complaintTemplates: {},
      processedClaims: [],
      processingClaims: false,
      hasProcessedReview: false,
      summaryTagSuggestions: [],
      summaryTags: [],
      newTag: '',
      draftResponse: '',
      edits: [],
      highlightMode: '',
      addFragmentText: '',
      finalResponse: '',
      generating: false,
      loadError: '',
      responseLengthPercent: 100,
      targetResponseLengthChars: 2000,
      botDetectedClaims: [],
      showAddClaimSelect: false,
      addClaimSelectedId: '',
      showHighlightedReview: true,
      showAddComplaintForm: false,
      newComplaintLabel: '',
      newComplaintRebuttal: '',
      newComplaintTone: 'apologize',
      addingNewComplaint: false,
      responseExamples: [],
      newExampleName: '',
      newExampleText: '',
      showAddExampleForm: false,
      addingNewExample: false,
      showExampleSelectFor: null,
    };
  },
  computed: {
    draftDisplayText() {
      return this.applyEdits(this.draftResponse, this.edits);
    },
    allCheckedClaims() {
      const fromKnown = (this.complaints || []).filter((c) => c.checked);
      const fromBot = (this.processedClaims || []).filter((c) => c.checked);
      return fromKnown.concat(fromBot);
    },
    checkedComplaints() {
      return (this.complaints || []).filter((c) => c.checked);
    },
    uncheckedComplaints() {
      return (this.complaints || []).filter((c) => !c.checked);
    },
    botDetectedClaimsWithPhrase() {
      return (this.botDetectedClaims || []).filter((c) => {
        const phrases = c.phrases || (c.phrase ? [c.phrase] : []);
        return Array.isArray(phrases) && phrases.some((p) => (p || '').trim().length > 0);
      });
    },
    highlightedReviewHtml() {
      const text = this.reviewText || '';
      const claims = this.botDetectedClaimsWithPhrase;
      if (!text || !claims.length) return this.escapeHtml(text);
      const segments = this.buildHighlightSegments(text, claims);
      return this.renderHighlightedText(text, segments);
    },
    allClaimTagsForBar() {
      const botDetected = this.botDetectedClaims || [];
      const checkedIds = new Set((this.complaints || []).filter((c) => c.checked).map((c) => c.id));
      const bot = botDetected
        .filter((c) => checkedIds.has(c.id))
        .map((c, i) => {
          const phrases = c.phrases || (c.phrase ? [c.phrase] : []);
          const firstPhrase = Array.isArray(phrases) && phrases.length ? phrases[0] : (c.phrase || '');
          return {
            type: 'bot',
            id: c.id,
            label: this.getClaimTagLabel(c),
            phrase: firstPhrase,
            phrases: phrases || [],
            colorClass: 'claim-tag-' + (botDetected.findIndex((x) => x.id === c.id) % CLAIM_HIGHLIGHT_COLORS.length),
          };
        });
      const botIds = new Set(botDetected.map((c) => c.id));
      const userAdded = this.checkedComplaints
        .filter((c) => !botIds.has(c.id))
        .map((c) => ({ type: 'complaint', id: c.id, label: c.label, phrase: '', colorClass: 'claim-tag-manual' }));
      const processedChecked = [];
      (this.processedClaims || []).forEach((c, idx) => {
        if (c.checked) processedChecked.push({ type: 'processed', processedIndex: idx, id: c.id, label: c.label, phrase: '', colorClass: 'claim-tag-manual' });
      });
      return bot.concat(userAdded).concat(processedChecked);
    },
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    escapeHtml(s) {
      const div = document.createElement('div');
      div.textContent = s;
      return div.innerHTML;
    },
    findPhraseRanges(text, phrase) {
      if (!phrase || !text) return [];
      const ranges = [];
      let pos = 0;
      while (pos < text.length) {
        const idx = text.indexOf(phrase, pos);
        if (idx === -1) break;
        ranges.push({ start: idx, end: idx + phrase.length });
        pos = idx + 1;
      }
      if (ranges.length > 0) return ranges;
      const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(escaped.replace(/\s+/g, '\\s+'), 'g');
      let match;
      while ((match = re.exec(text)) !== null) {
        ranges.push({ start: match.index, end: match.index + match[0].length });
      }
      return ranges;
    },
    buildHighlightSegments(text, claimsWithPhrase) {
      const raw = [];
      const colorIdx = (i) => i % CLAIM_HIGHLIGHT_COLORS.length;
      claimsWithPhrase.forEach((c, colorIndex) => {
        const phrases = c.phrases || (c.phrase ? [c.phrase] : []);
        if (!Array.isArray(phrases)) return;
        phrases.forEach((p) => {
          const phrase = (p || '').trim();
          if (!phrase) return;
          this.findPhraseRanges(text, phrase).forEach((r) => {
            raw.push({ start: r.start, end: r.end, colorIndex: colorIdx(colorIndex) });
          });
        });
      });
      raw.sort((a, b) => a.start - b.start);
      const merged = [];
      raw.forEach((seg) => {
        let start = seg.start;
        const end = seg.end;
        for (let i = merged.length - 1; i >= 0; i--) {
          if (merged[i].end <= start) break;
          if (merged[i].start < end) {
            if (start < merged[i].end) start = merged[i].end;
            if (start >= end) return;
          }
        }
        if (start < end) merged.push({ start, end, colorIndex: seg.colorIndex });
      });
      merged.sort((a, b) => a.start - b.start);
      return merged;
    },
    renderHighlightedText(text, segments) {
      if (!segments.length) return this.escapeHtml(text);
      let out = '';
      let pos = 0;
      segments.forEach((seg) => {
        if (seg.start > pos) out += this.escapeHtml(text.slice(pos, seg.start));
        const cls = 'review-highlight-' + seg.colorIndex;
        out += '<span class="' + cls + '">' + this.escapeHtml(text.slice(seg.start, seg.end)) + '</span>';
        pos = seg.end;
      });
      if (pos < text.length) out += this.escapeHtml(text.slice(pos));
      return out;
    },
    removeClaimTag(item) {
      if (item.type === 'bot' || item.type === 'complaint') {
        const c = this.complaints.find((x) => x.id === item.id);
        if (c) c.checked = false;
      } else if (item.type === 'processed' && item.processedIndex != null) {
        const p = this.processedClaims[item.processedIndex];
        if (p) p.checked = false;
      }
    },
    getClaimTagTitle(item) {
      if (item.phrases && item.phrases.length) {
        return 'По фразам: ' + item.phrases.map((p) => '«' + p + '»').join('; ');
      }
      if (item.phrase) return 'По фразе: «' + item.phrase + '»';
      return 'Добавлено вручную';
    },
    getClaimTagLabel(c) {
      if (c.id && c.id !== 'custom') {
        const comp = this.complaints.find((x) => x.id === c.id);
        return comp ? comp.label : (c.label || c.id);
      }
      return c.label || 'Другое';
    },
    onAddClaimFromList() {
      const id = this.addClaimSelectedId;
      if (!id) return;
      const comp = this.complaints.find((c) => c.id === id);
      if (comp) {
        comp.checked = true;
        comp.selectedTone = 'apologize';
        const opts = (comp.rebuttalsByTone && comp.rebuttalsByTone.apologize) || [];
        comp.selectedRebuttal = opts[0] ? opts[0].id : '';
      }
      this.addClaimSelectedId = '';
      this.showAddClaimSelect = false;
    },
    rebuttalOptionsFor(complaint) {
      if (!complaint || !complaint.selectedTone) return [];
      const byTone = complaint.rebuttalsByTone || {};
      const list = byTone[complaint.selectedTone];
      return Array.isArray(list) ? list : [];
    },
    onComplaintToneChange(complaint) {
      complaint.selectedRebuttal = '';
    },
    getExampleById(id) {
      return (this.responseExamples || []).find((e) => e.id === id) || null;
    },
    cancelAddExampleForm() {
      this.showAddExampleForm = false;
      this.newExampleName = '';
      this.newExampleText = '';
    },
    async addNewExample() {
      const name = (this.newExampleName || '').trim();
      const text = (this.newExampleText || '').trim();
      if (!name || !text) return;
      this.addingNewExample = true;
      try {
        const base = process.env.NODE_ENV === 'development' ? 'https://manager.1akb.by' : '';
        const res = await fetch(base + '/review-response-settings/add-example', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, text }),
        });
        const respText = await res.text();
        if (respText.trim().startsWith('<')) throw new Error('Сервер вернул HTML');
        const data = JSON.parse(respText);
        if (data.error) throw new Error(data.error);
        if (data.example) {
          this.responseExamples.push(data.example);
          this.cancelAddExampleForm();
        }
        if (this.$toast && this.$toast.success) this.$toast.success('Пример добавлен');
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error(e.message || 'Ошибка сохранения');
      } finally {
        this.addingNewExample = false;
      }
    },
    async loadSettings() {
      try {
        const base = process.env.NODE_ENV === 'development' ? 'https://manager.1akb.by' : '';
        const res = await fetch(`${base}/review-response-settings`);
        if (!res.ok) throw new Error('Не удалось загрузить настройки (ответ ' + res.status + ')');
        const text = await res.text();
        if (text.trim().startsWith('<')) {
          throw new Error('Сервер вернул страницу вместо данных. Проверьте, что бэкенд (Node, порт 4000) запущен и nginx проксирует /review-response-settings на него.');
        }
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseErr) {
          throw new Error('Ответ сервера не JSON. Убедитесь, что API доступен.');
        }
        this.roles = data.roles || [];
        this.tones = data.tones || [];
        this.summaryTagSuggestions = data.summaryTagSuggestions || [];
        this.responseExamples = Array.isArray(data.responseExamples) ? data.responseExamples : [];
        const complaintsRaw = data.complaints || [];
        this.complaintTemplates = {};
        this.complaints = complaintsRaw.map((c) => {
          let rebuttalsByTone = c.rebuttalsByTone;
          if (!rebuttalsByTone && c.rebuttals && c.rebuttals.length) {
            rebuttalsByTone = { apologize: c.rebuttals.map((r, i) => (typeof r === 'string' ? { id: String(i), label: r } : r)) };
          }
          rebuttalsByTone = rebuttalsByTone || {};
          this.complaintTemplates[c.id] = { label: c.label, rebuttalsByTone: rebuttalsByTone };
          return {
            id: c.id,
            label: c.label,
            checked: false,
            rebuttalsByTone: rebuttalsByTone,
            selectedTone: 'apologize',
            selectedRebuttal: '',
            customRebuttal: '',
            addingRebuttal: false,
            selectedExampleId: '',
          };
        });
        this.loadError = '';
        this.hasProcessedReview = false;
        this.botDetectedClaims = [];
      } catch (e) {
        this.loadError = e.message || 'Ошибка загрузки';
      }
    },
    toggleSummaryTag(tag) {
      const i = this.summaryTags.indexOf(tag);
      if (i >= 0) this.summaryTags.splice(i, 1);
      else this.summaryTags.push(tag);
    },
    addSummaryTag() {
      const t = (this.newTag || '').trim();
      if (t && !this.summaryTags.includes(t)) {
        this.summaryTags.push(t);
        this.newTag = '';
      }
    },
    async addRebuttalToPredefined(complaint) {
      const text = (complaint.customRebuttal || '').trim();
      if (!text) return;
      complaint.addingRebuttal = true;
      try {
        const base = process.env.NODE_ENV === 'development' ? 'https://manager.1akb.by' : '';
        const res = await fetch(base + '/review-response-settings/add-rebuttal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ complaintId: complaint.id, toneId: complaint.selectedTone || 'apologize', rebuttalText: text }),
        });
        const respText = await res.text();
        if (respText.trim().startsWith('<')) throw new Error('Сервер вернул HTML');
        const data = JSON.parse(respText);
        if (data.error) throw new Error(data.error);
        const toneId = complaint.selectedTone || 'apologize';
        if (!complaint.rebuttalsByTone[toneId]) complaint.rebuttalsByTone[toneId] = [];
        const list = complaint.rebuttalsByTone[toneId];
        const newId = String(list.length);
        const newOpt = { id: newId, label: text };
        list.push(newOpt);
        complaint.selectedRebuttal = newId;
        complaint.customRebuttal = '';
        if (this.complaintTemplates[complaint.id]) {
          this.complaintTemplates[complaint.id].rebuttalsByTone = complaint.rebuttalsByTone;
        }
        if (this.$toast && this.$toast.success) this.$toast.success('Вариант сохранён в заготовки');
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error(e.message || 'Ошибка сохранения');
      } finally {
        complaint.addingRebuttal = false;
      }
    },
    cancelAddComplaintForm() {
      this.showAddComplaintForm = false;
      this.newComplaintLabel = '';
      this.newComplaintRebuttal = '';
      this.newComplaintTone = 'apologize';
    },
    async addNewComplaintToCatalog() {
      const label = (this.newComplaintLabel || '').trim();
      if (!label) return;
      this.addingNewComplaint = true;
      try {
        const base = process.env.NODE_ENV === 'development' ? 'https://manager.1akb.by' : '';
        const firstRebuttal = (this.newComplaintRebuttal || '').trim();
        const toneId = (this.newComplaintTone && this.tones.some((t) => t.id === this.newComplaintTone))
          ? this.newComplaintTone
          : 'apologize';
        const res = await fetch(base + '/review-response-settings/add-complaint', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: label, firstRebuttal: firstRebuttal || undefined, toneId: toneId }),
        });
        const text = await res.text();
        if (text.trim().startsWith('<')) throw new Error('Сервер вернул HTML');
        const data = JSON.parse(text);
        if (data.error) throw new Error(data.error);
        const newComplaint = data.complaint;
        if (newComplaint) {
          const rebuttalsByTone = newComplaint.rebuttalsByTone || (newComplaint.rebuttals && newComplaint.rebuttals.length
            ? { apologize: (newComplaint.rebuttals || []).map((r, i) => (typeof r === 'string' ? { id: String(i), label: r } : r)) }
            : {});
          this.complaints.push({
            id: newComplaint.id,
            label: newComplaint.label,
            checked: false,
            rebuttalsByTone: rebuttalsByTone,
            selectedTone: 'apologize',
            selectedRebuttal: '',
            customRebuttal: '',
            selectedExampleId: '',
          });
          this.complaintTemplates[newComplaint.id] = { label: newComplaint.label, rebuttalsByTone: rebuttalsByTone };
          this.cancelAddComplaintForm();
        }
        if (this.$toast && this.$toast.success) this.$toast.success('Претензия добавлена в справочник');
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error(e.message || 'Ошибка сохранения');
      } finally {
        this.addingNewComplaint = false;
      }
    },
    async addToPredefined(complaint, idx) {
      const label = (complaint.label || '').trim();
      if (!label) return;
      complaint.adding = true;
      try {
        const base = process.env.NODE_ENV === 'development' ? 'https://manager.1akb.by' : '';
        const firstRebuttal = (complaint.customRebuttal || '').trim();
        const res = await fetch(base + '/review-response-settings/add-complaint', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: label, firstRebuttal: firstRebuttal || undefined }),
        });
        const text = await res.text();
        if (text.trim().startsWith('<')) throw new Error('Сервер вернул HTML');
        const data = JSON.parse(text);
        if (data.error) throw new Error(data.error);
        const newComplaint = data.complaint;
        if (newComplaint) {
          const rebuttalsByTone = newComplaint.rebuttalsByTone || (newComplaint.rebuttals && newComplaint.rebuttals.length
            ? { apologize: (newComplaint.rebuttals || []).map((r, i) => (typeof r === 'string' ? { id: String(i), label: r } : r)) }
            : {});
          this.complaints.push({
            id: newComplaint.id,
            label: newComplaint.label,
            checked: false,
            rebuttalsByTone: rebuttalsByTone,
            selectedTone: 'apologize',
            selectedRebuttal: '',
            customRebuttal: '',
            selectedExampleId: '',
          });
          this.complaintTemplates[newComplaint.id] = { label: newComplaint.label, rebuttalsByTone: rebuttalsByTone };
          this.processedClaims.splice(idx, 1);
        }
        if (this.$toast && this.$toast.success) this.$toast.success('Добавлено в предустановленные');
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error(e.message || 'Ошибка сохранения');
      } finally {
        complaint.adding = false;
      }
    },
    async processReview() {
      const review = this.reviewText.trim();
      if (!review) return;
      this.processingClaims = true;
      this.processedClaims = [];
      try {
        const knownComplaints = (this.complaints || []).map((c) => ({ id: c.id, label: c.label }));
        const res = await fetch(AI_API_URL + '/extract-review-claims', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ review: review, knownComplaints: knownComplaints }),
        });
        const text = await res.text();
        if (text.trim().startsWith('<')) {
          throw new Error('Сервер вернул HTML вместо ответа.');
        }
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseErr) {
          throw new Error('Ответ API не JSON.');
        }
        if (data.status === 'error' || data.error) throw new Error(data.message || data.error || 'Ошибка');
        const claims = data.claims || [];
        this.botDetectedClaims = claims.slice();
        const templates = this.complaintTemplates || {};
        // Предустановленные: если бот вернул претензию с известным id — отмечаем галочкой и выбираем первый ответ
        claims.forEach((c) => {
          if (c.id && c.id !== 'custom') {
            const comp = this.complaints.find((x) => x.id === c.id);
            if (comp) {
              comp.checked = true;
              comp.selectedTone = 'apologize';
              const opts = (comp.rebuttalsByTone && comp.rebuttalsByTone.apologize) || [];
              comp.selectedRebuttal = opts[0] ? opts[0].id : '';
            }
          }
        });
        this.hasProcessedReview = true;
        const hasPhrases = (this.botDetectedClaims || []).some((c) => (c.phrase || '').trim().length > 0);
        if (hasPhrases) this.showHighlightedReview = true;
        // Дополнительные: только те, которых нет в предустановленных (custom)
        this.processedClaims = claims
          .filter((c) => c.id === 'custom')
          .map((c) => {
            const tpl = templates[c.id];
            const rebuttalsByTone = (tpl && tpl.rebuttalsByTone) ? tpl.rebuttalsByTone : {};
            return {
              id: c.id,
              label: c.label,
              checked: true,
              rebuttalsByTone: rebuttalsByTone,
              selectedTone: 'apologize',
              selectedRebuttal: '',
              customRebuttal: '',
              adding: false,
              selectedExampleId: '',
            };
          });
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error(e.message || 'Ошибка обработки');
      } finally {
        this.processingClaims = false;
      }
    },
    setHighlightMode(mode) {
      this.highlightMode = this.highlightMode === mode ? '' : mode;
    },
    onDraftMouseUp() {
      if (!this.highlightMode) return;
      const sel = window.getSelection();
      const text = sel.toString().trim();
      if (!text) return;
      if (this.highlightMode === 'add') {
        this.edits.push({ type: 'add', afterText: text, insertText: this.addFragmentText || '' });
      } else {
        this.edits.push({
          type: this.highlightMode === 'exclude' ? 'exclude' : 'change',
          text,
          replacement: this.highlightMode === 'change' ? (this.addFragmentText || '') : undefined,
        });
      }
      sel.removeAllRanges();
      this.highlightMode = '';
      this.addFragmentText = '';
    },
    applyEdits(draftText, edits) {
      if (!draftText) return '';
      if (!edits || !edits.length) return draftText;
      let text = draftText;
      for (const ed of edits) {
        if (ed.type === 'exclude' && ed.text) {
          text = text.replace(ed.text, '');
        } else if (ed.type === 'change' && ed.text !== undefined) {
          const replacement = ed.replacement != null ? String(ed.replacement) : '';
          text = text.replace(ed.text, replacement);
        } else if (ed.type === 'add' && ed.afterText != null && ed.insertText != null) {
          const pos = text.indexOf(ed.afterText);
          if (pos >= 0) {
            const insertAt = pos + ed.afterText.length;
            text = text.slice(0, insertAt) + ed.insertText + text.slice(insertAt);
          }
        }
      }
      return text;
    },
    async generateResponse(opts = {}) {
      this.generating = true;
      if (!opts.keepDraft) this.finalResponse = '';
      try {
        const body = {
          review: this.reviewText,
          ticket: this.ticket,
          role: this.responseRole,
          complaints: this.allCheckedClaims.map((c) => {
            const sel = c.selectedRebuttal != null ? String(c.selectedRebuttal) : '';
            const optsList = this.rebuttalOptionsFor(c);
            const fromDropdown =
              optsList.length && sel && sel !== '__custom__'
                ? (optsList.find((o) => String(o.id) === sel) || {}).label
                : '';
            const rebuttal = (c.customRebuttal && c.customRebuttal.trim()) || (fromDropdown && fromDropdown.trim()) || '';
            const example = c.selectedExampleId ? this.getExampleById(c.selectedExampleId) : null;
            const exampleText = example ? example.text : '';
            return {
              id: c.id,
              label: c.label,
              tone: c.selectedTone || '',
              rebuttal: rebuttal,
              exampleText: exampleText || undefined,
            };
          }),
          summaryTags: this.summaryTags,
        };
        if (opts.lengthPercent != null && opts.lengthPercent < 100) {
          const currentLength = (this.draftResponse || '').length;
          const targetCharacters = Math.round(currentLength * opts.lengthPercent / 100);
          body.currentDraft = this.draftResponse;
          body.targetCharacters = targetCharacters;
          body.lengthInstruction = `Перепиши ответ ниже строго до ${targetCharacters} знаков (не больше). Сейчас в нём ${currentLength} знаков. Итог должен быть не более ${targetCharacters} символов.`;
        } else {
          body.targetCharacters = Math.min(3000, Math.max(500, Number(this.targetResponseLengthChars) || 2000));
        }
        const res = await fetch(AI_API_URL + '/generate-review-response', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const text = await res.text();
        if (text.trim().startsWith('<')) {
          throw new Error('ai.1akb.by вернул HTML вместо ответа. Проверьте доступность сервиса.');
        }
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseErr) {
          throw new Error('Ответ API не JSON.');
        }
        if (data.status === 'error' || data.error) throw new Error(data.message || data.error || 'Ошибка');
        this.draftResponse = data.text || '';
        this.edits = [];
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error(e.message || 'Ошибка генерации');
      } finally {
        this.generating = false;
      }
    },
    regenerateWithLength() {
      this.generateResponse({ lengthPercent: this.responseLengthPercent, keepDraft: true });
    },
    generateFinalResponse() {
      this.finalResponse = this.applyEdits(this.draftResponse, this.edits);
    },
  },
};
</script>

<style scoped>
.review-response-root {
  padding: 0 16px 20px;
  max-width: 100%;
  box-sizing: border-box;
}

.review-response-root *,
.review-response-root *::before,
.review-response-root *::after {
  box-sizing: border-box;
}

.header-main {
  display: flex;
  align-items: center;
  padding: 16px 0 12px;
  min-height: 48px;
}

.hm-left {
  color: #1e2248;
  flex: 1;
  min-width: 0;
}

.header-main__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
}

.field-rules-section + .field-rules-section {
  margin-top: 12px;
}

.hm-right {
  flex-shrink: 0;
}

.field-rules-section {
  margin-top: 12px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.field-rules-title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #1e2248;
}

.field-rule-block {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fafafa;
}

.field-rule-row {
  margin-bottom: 8px;
}

.field-rule-row:last-child {
  margin-bottom: 0;
}

.field-rule-row-inline {
  display: inline-block;
  margin-right: 16px;
}

.field-rule-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  font-size: 12px;
}

.field-rule-input,
.field-rule-select,
.field-rule-textarea {
  width: 100%;
  max-width: 560px;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 13px;
  box-sizing: border-box;
}

.field-rule-textarea {
  min-height: 88px;
  max-height: 200px;
  resize: vertical;
}

.review-highlight-inplace {
  width: 100%;
  max-width: 560px;
  min-height: 88px;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  background: #fafafa;
  box-sizing: border-box;
  color: #333;
}

.review-highlight-inplace ::v-deep .review-highlight-0,
.review-highlight-inplace ::v-deep .review-highlight-1,
.review-highlight-inplace ::v-deep .review-highlight-2,
.review-highlight-inplace ::v-deep .review-highlight-3,
.review-highlight-inplace ::v-deep .review-highlight-4,
.review-highlight-inplace ::v-deep .review-highlight-5,
.review-highlight-inplace ::v-deep .review-highlight-6,
.review-highlight-inplace ::v-deep .review-highlight-7 {
  padding: 2px 6px;
  margin: 0 1px;
  border-radius: 4px;
  border-left: 3px solid;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}

.review-highlight-inplace ::v-deep .review-highlight-0 { background: #ffebee; border-left-color: #ef9a9a; }
.review-highlight-inplace ::v-deep .review-highlight-1 { background: #e3f2fd; border-left-color: #90caf9; }
.review-highlight-inplace ::v-deep .review-highlight-2 { background: #e8f5e9; border-left-color: #a5d6a7; }
.review-highlight-inplace ::v-deep .review-highlight-3 { background: #fff3e0; border-left-color: #ffcc80; }
.review-highlight-inplace ::v-deep .review-highlight-4 { background: #f3e5f5; border-left-color: #ce93d8; }
.review-highlight-inplace ::v-deep .review-highlight-5 { background: #e0f7fa; border-left-color: #80deea; }
.review-highlight-inplace ::v-deep .review-highlight-6 { background: #fce4ec; border-left-color: #f48fb1; }
.review-highlight-inplace ::v-deep .review-highlight-7 { background: #e8eaf6; border-left-color: #9fa8da; }

.review-edit-text-btn {
  margin-top: 4px;
  padding: 2px 0;
  background: none;
  border: none;
  color: #1565c0;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}

.review-edit-text-btn:hover {
  color: #0d47a1;
}

.process-btn {
  padding: 6px 14px;
  background: #2e7d32;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.process-btn:hover:not(:disabled) {
  background: #1b5e20;
}

.process-btn:disabled {
  background: #9e9e9e;
  cursor: not-allowed;
}

.detected-claims-row {
  min-height: 48px;
}

.detected-claims-row .field-rule-label {
  margin-bottom: 4px;
}

.detected-claims-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.claim-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 10px;
  border-radius: 14px;
  font-size: 12px;
  border: 1px solid transparent;
  flex-shrink: 0;
}

.claim-tag-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.15);
  color: inherit;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.claim-tag-remove:hover {
  background: rgba(0, 0, 0, 0.25);
}

.claim-tag-0 { background: #ffebee; color: #b71c1c; border-color: #ef9a9a; }
.claim-tag-1 { background: #e3f2fd; color: #0d47a1; border-color: #90caf9; }
.claim-tag-2 { background: #e8f5e9; color: #1b5e20; border-color: #a5d6a7; }
.claim-tag-3 { background: #fff3e0; color: #e65100; border-color: #ffcc80; }
.claim-tag-4 { background: #f3e5f5; color: #4a148c; border-color: #ce93d8; }
.claim-tag-5 { background: #e0f7fa; color: #006064; border-color: #80deea; }
.claim-tag-6 { background: #fce4ec; color: #880e4f; border-color: #f48fb1; }
.claim-tag-7 { background: #e8eaf6; color: #283593; border-color: #9fa8da; }
.claim-tag-manual { background: #eeeeee; color: #424242; border-color: #bdbdbd; }

.add-claim-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.add-claim-btn {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid #2e7d32;
  background: #fff;
  color: #2e7d32;
  border-radius: 5px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  font-weight: bold;
}

.add-claim-btn:hover {
  background: #2e7d32;
  color: #fff;
}

.add-claim-select {
  min-width: 220px;
  max-width: 360px;
}

.matrix-hint {
  margin-bottom: 8px;
  color: #555;
  font-size: 12px;
  line-height: 1.4;
}

.complaint-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #eee;
  min-height: 36px;
}

.complaint-row:last-child {
  border-bottom: none;
}

.complaint-left {
  flex: 0 0 260px;
  min-width: 0;
}

.complaint-right {
  flex: 1;
  min-width: 0;
}

.complaint-check {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
}

.complaint-check input {
  margin: 0;
  flex-shrink: 0;
}

.complaint-select {
  max-width: 360px;
  flex-shrink: 0;
}

.complaint-custom {
  margin-top: 4px;
  max-width: 100%;
  flex: 1;
  min-width: 120px;
}

.complaint-row .complaint-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.complaint-right-fields {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.complaint-tone {
  min-width: 150px;
}

.add-complaint-catalog-row {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.add-complaint-catalog-btn {
  padding: 6px 12px;
  border: 1px dashed #2e7d32;
  background: #fff;
  color: #2e7d32;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.add-complaint-catalog-btn:hover {
  background: #e8f5e9;
  border-style: solid;
}

.add-complaint-catalog-form {
  padding: 10px 12px;
  background: #f5f5f5;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.add-complaint-catalog-form .field-rule-input,
.add-complaint-catalog-form .field-rule-textarea {
  max-width: 100%;
}

.add-complaint-textarea {
  min-height: 60px;
}

.add-complaint-catalog-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.add-complaint-cancel-btn {
  padding: 6px 12px;
  border: 1px solid #9e9e9e;
  background: #fff;
  color: #616161;
  border-radius: 5px;
  font-size: 12px;
  cursor: pointer;
}

.add-complaint-cancel-btn:hover:not(:disabled) {
  background: #f5f5f5;
}

.add-complaint-cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.add-to-predefined-btn {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid #2e7d32;
  background: #fff;
  color: #2e7d32;
  border-radius: 5px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  font-weight: bold;
}

.add-to-predefined-btn:hover:not(:disabled) {
  background: #2e7d32;
  color: #fff;
}

.add-to-predefined-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.add-rebuttal-btn {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid #1565c0;
  background: #fff;
  color: #1565c0;
  border-radius: 5px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  font-weight: bold;
}

.add-rebuttal-btn:hover:not(:disabled) {
  background: #1565c0;
  color: #fff;
}

.add-rebuttal-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.examples-block {
  margin-bottom: 10px;
}

.example-item {
  margin-bottom: 6px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 5px;
}

.example-item:last-child {
  margin-bottom: 0;
}

.example-item-name {
  display: block;
  margin-bottom: 4px;
  color: #1e2248;
  font-size: 12px;
  font-weight: 600;
}

.example-item-text {
  margin: 0;
  font-size: 12px;
  color: #555;
  line-height: 1.45;
  white-space: pre-wrap;
}

.example-item-empty {
  color: #888;
  font-style: italic;
  font-size: 12px;
}

.add-example-row {
  margin-top: 8px;
}

.complaint-example-wrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.add-example-to-complaint-btn {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid #1565c0;
  background: #fff;
  color: #1565c0;
  border-radius: 5px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  font-weight: bold;
}

.add-example-to-complaint-btn:hover,
.add-example-to-complaint-btn.active {
  background: #1565c0;
  color: #fff;
}

.complaint-example-select {
  min-width: 180px;
  max-width: 280px;
}

.example-attached-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 4px;
  font-size: 11px;
  border: 1px solid #90caf9;
}

.complaints-subtitle {
  margin: 20px 0 12px;
  font-size: 16px;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.tags-block {
  margin-top: 8px;
}

.tags-suggested {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #e0e0e0;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
}

.tag-chip--selected {
  background: #bbdefb;
  color: #1565c0;
}

.tag-remove {
  margin-left: 4px;
  font-weight: bold;
}

.tags-input-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.tags-input {
  max-width: 320px;
}

.add-tag-btn {
  padding: 8px 16px;
  background: #2979ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.tags-selected {
  font-size: 13px;
  color: #666;
}

.save-matrix-btn {
  padding: 6px 14px;
  background: #2979ff;
  color: #fff;
  border: none;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.save-matrix-btn:disabled {
  background: #90a4ae;
  cursor: not-allowed;
}

.target-length-row {
  margin-bottom: 10px;
}

.target-length-row .field-rule-label {
  margin-bottom: 4px;
}

.target-length-slider {
  width: 100%;
  max-width: 360px;
  height: 6px;
  accent-color: #2979ff;
}

.generate-response-btn {
  margin-top: 4px;
}

.length-regenerate-block {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f0f7ff;
  border: 1px solid #bbdefb;
  border-radius: 5px;
}

.length-regenerate-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  font-size: 12px;
}

.length-regenerate-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.length-regenerate-slider {
  flex: 1;
  max-width: 280px;
  height: 6px;
  accent-color: #1565c0;
}

.length-regenerate-value {
  min-width: 36px;
  font-weight: 600;
  color: #1565c0;
  font-size: 12px;
}

.length-hint {
  margin-bottom: 8px;
}

.length-regenerate-btn {
  margin-top: 4px;
}

.highlight-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.hl-btn {
  padding: 4px 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
}

.hl-btn.active {
  color: #fff;
  border-color: transparent;
}

.hl-btn--yellow.active { background: #f9a825; }
.hl-btn--red.active { background: #c62828; }
.hl-btn--blue.active { background: #1565c0; }

.hl-fragment-input {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  width: 180px;
  font-size: 12px;
}

.draft-area {
  min-height: 100px;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  background: #fafafa;
  white-space: pre-wrap;
  margin-bottom: 10px;
  font-size: 13px;
  line-height: 1.5;
}

.draft-area.selecting {
  user-select: text;
}

.edits-list {
  margin-top: 8px;
  font-size: 12px;
  color: #555;
}

.edits-list-title {
  font-weight: 600;
  margin-right: 6px;
}

.edit-badge {
  display: inline-block;
  margin-right: 6px;
  margin-bottom: 2px;
  padding: 2px 6px;
  background: #f5f5f5;
  border-radius: 3px;
  font-size: 11px;
}

.draft-actions {
  margin-top: 8px;
}

.final-block .final-text {
  white-space: pre-wrap;
  padding: 10px 12px;
  background: #f5f5f5;
  border-radius: 5px;
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.5;
}

.ticket-footer {
  font-size: 12px;
  color: #666;
}

.matrix-loading {
  padding: 16px;
  text-align: center;
  color: #c62828;
  font-size: 13px;
}
</style>
